import React, { useState, useEffect, useCallback } from 'react';
import { Hourglass, CheckCircle, Clock, Users, X, AlertTriangle } from 'lucide-react';
import api from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { toast } from 'react-toastify';

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString();
};

const ProbationStatus = ({ onSidebarCountsRefresh }) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [extendTarget, setExtendTarget] = useState(null);
  const [extendDays, setExtendDays] = useState('');
  const [extendReason, setExtendReason] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await api.get('/admin/probation');
      setEmployees(response.data.employees || []);
    } catch (error) {
      console.error('Error fetching probation list:', error);
      toast.error('Failed to fetch probation status list');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const readyForConfirmation = employees.filter((e) => e.readyForConfirmation);

  const filterTabs = [
    { key: 'all', label: 'All Employees', icon: Users },
    { key: 'probation', label: 'On Probation', icon: Hourglass },
    { key: 'confirmed', label: 'Confirmed', icon: CheckCircle },
  ];

  const filteredEmployees = employees.filter((e) => {
    if (filter === 'all') return true;
    return e.probationStatus === filter;
  });

  const handleConfirm = async (employee) => {
    if (!employee.isActive) {
      toast.error('This employee is inactive. Reactivate their account before confirming.');
      return;
    }
    if (!window.confirm(`Confirm ${employee.name} as a permanent employee? This will unlock Earned Leave immediately.`)) {
      return;
    }
    setActionLoadingId(employee.id);
    try {
      await api.put(`/admin/probation/${employee.id}/confirm`);
      toast.success(`${employee.name} has been confirmed`);
      await fetchEmployees();
      if (onSidebarCountsRefresh) onSidebarCountsRefresh();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to confirm employee');
    } finally {
      setActionLoadingId(null);
    }
  };

  const openExtendModal = (employee) => {
    if (!employee.isActive) {
      toast.error('This employee is inactive. Reactivate their account before extending probation.');
      return;
    }
    setExtendTarget(employee);
    setExtendDays(String(employee.probationDays ?? 60));
    setExtendReason('');
  };

  const closeExtendModal = () => {
    setExtendTarget(null);
    setExtendDays('');
    setExtendReason('');
  };

  const handleExtendSubmit = async (e) => {
    e.preventDefault();
    if (!extendTarget) return;
    if (!extendDays || Number(extendDays) < 0) {
      toast.error('Enter a valid number of probation days');
      return;
    }
    setActionLoadingId(extendTarget.id);
    try {
      await api.put(`/admin/probation/${extendTarget.id}/extend`, {
        newProbationDays: Number(extendDays),
        reason: extendReason
      });
      toast.success(`Probation extended for ${extendTarget.name}`);
      closeExtendModal();
      await fetchEmployees();
      if (onSidebarCountsRefresh) onSidebarCountsRefresh();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to extend probation');
    } finally {
      setActionLoadingId(null);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Probation Status</h1>
        <p className="text-gray-600">Review employees on probation, confirm them, or extend their probation period</p>
      </div>

      {readyForConfirmation.length > 0 && !bannerDismissed && (
        <div className="flex items-start justify-between gap-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-800">
              <strong>{readyForConfirmation.length}</strong> employee{readyForConfirmation.length > 1 ? 's are' : ' is'} ready for confirmation - their probation period has ended.
            </p>
          </div>
          <button onClick={() => setBannerDismissed(true)} className="text-amber-500 hover:text-amber-700 flex-shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {filterTabs.map(({ key, label, icon: Icon }) => {
          const isActive = filter === key;
          return (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                isActive ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Join Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Probation Days</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Probation End Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredEmployees.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">No employees found</td>
                </tr>
              )}
              {filteredEmployees.map((employee) => (
                <tr key={employee.id} className={!employee.isActive ? 'bg-gray-50 opacity-60' : employee.readyForConfirmation ? 'bg-amber-50' : ''}>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">{employee.name}</div>
                    <div className="text-xs text-gray-500">{employee.employeeId}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(employee.joinDate)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{employee.probationDays}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(employee.probationEndDate)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                      employee.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {employee.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {employee.probationStatus === 'confirmed' ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                        <CheckCircle className="h-3.5 w-3.5" /> Confirmed
                      </span>
                    ) : employee.readyForConfirmation ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                        <Clock className="h-3.5 w-3.5" /> Ready for Confirmation
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                        <Hourglass className="h-3.5 w-3.5" /> On Probation
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {employee.probationStatus !== 'confirmed' && (
                      <div className="flex justify-end gap-2" title={!employee.isActive ? 'Reactivate this employee to manage their probation' : undefined}>
                        <button
                          onClick={() => openExtendModal(employee)}
                          disabled={actionLoadingId === employee.id || !employee.isActive}
                          className="px-3 py-1.5 text-xs font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Extend
                        </button>
                        <button
                          onClick={() => handleConfirm(employee)}
                          disabled={actionLoadingId === employee.id || !employee.isActive}
                          className="px-3 py-1.5 text-xs font-semibold rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Confirm
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {extendTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Extend Probation - {extendTarget.name}</h3>
              <button onClick={closeExtendModal} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleExtendSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New total probation days (from join date)</label>
                <input
                  type="number"
                  min="0"
                  value={extendDays}
                  onChange={(e) => setExtendDays(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-gray-400">Current: {extendTarget.probationDays} days, ending {formatDate(extendTarget.probationEndDate)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
                <textarea
                  value={extendReason}
                  onChange={(e) => setExtendReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={closeExtendModal} className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoadingId === extendTarget.id}
                  className="px-4 py-2 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProbationStatus;
